import { SelectType } from 'application/ui/fields/select'
import { CartList } from './_list'
import {
  DeliveryMethod,
  CartItem,
  OrderFormResponse,
  OrderFormSuccessResponse
} from './types'
import { validateForm, showError } from 'application/utils/form-validation'
import { getCartItems } from './_service'

export class CartForm {
  private container: HTMLElement
  private list: CartList
  private deliveryMethod: DeliveryMethod = 'postal'
  private citySelectEl: SelectType
  private locationSelectEl: SelectType
  private quoteToken: string | null = null
  private quotedStocks: string | null = null
  private submitBtn: HTMLButtonElement

  constructor(container: HTMLElement, list: CartList) {
    this.container = container
    this.list = list

    this.init()
  }

  private init() {
    this.submitBtn = this.container.querySelector<HTMLButtonElement>(
      'button[type="submit"]'
    )!

    const items = this.list.getItems()
    this.defineDeliveryMethod(items)
    this.updatePaymentMethods(items)

    this.container.addEventListener('submit', this.onSubmit)
  }

  public update(items: CartItem[]) {
    this.defineDeliveryMethod(items)
    this.updatePaymentMethods(items)
  }

  private updatePaymentMethods(items: CartItem[]) {
    const sbp = this.container.querySelector<HTMLElement>(
      '[data-cart-payment-sbp]'
    )
    if (!sbp) return

    const belarus = items.some((item) => item.currency === 'BYN')
    sbp.classList.toggle('is-hidden', belarus)
    if (!belarus || !sbp.querySelector<HTMLInputElement>('input')?.checked)
      return

    const card = this.container.querySelector<HTMLInputElement>(
      'input[name="payment_method"][value="card"]'
    )
    if (card) card.checked = true
  }

  private defineDeliveryMethod(items: CartItem[]) {
    this.deliveryMethod = 'postal' // default
    if (items.some((item) => item.type === 'box')) {
      this.deliveryMethod = 'pickup'
    }

    this.list.setDeliveryMethod(this.deliveryMethod)
    this.updateForm()

    const deliveryMethodGroup = this.container.querySelector<HTMLElement>(
      '[data-cart-field="delivery-method"]'
    )
    deliveryMethodGroup?.addEventListener('change', this.onDeliveryMethodChange)

    this.citySelectEl = this.container.querySelector<SelectType>(
      '[data-field-name="city"] .select-field'
    )!
    this.locationSelectEl = this.container.querySelector<SelectType>(
      '[data-field-name="location"] .select-field'
    )!
    this.citySelectEl.addEventListener(
      'selectfield:change',
      this.onCityChange as EventListener
    )
  }

  private onDeliveryMethodChange = (event: Event) => {
    const target = event.target as HTMLInputElement
    if (target.name === 'delivery_method') {
      this.deliveryMethod = target.value as DeliveryMethod
      this.list.setDeliveryMethod(this.deliveryMethod)
      this.updateForm()
    }
  }

  private onCityChange = (event: CustomEvent) => {
    const { value } = event.detail
    const locations = this.locationSelectEl.dataset.storeOptions
      ? JSON.parse(this.locationSelectEl.dataset.storeOptions)
      : []

    this.locationSelectEl.select.setOptions(locations[value] || [])
  }

  private onSubmit = (event: Event): void => {
    event.preventDefault()
    const form = event.target as HTMLFormElement

    if (this.submitBtn.disabled) {
      return
    }

    if (!validateForm(form)) {
      return
    }

    const action = form.dataset.makeOrderApi || ''

    this.fetchForm(form, action).then((data) => {
      if (data && !data.payment_required) {
        this.confirmOrderPayment(String(data.id))
      }
      if (data?.payment_url) {
        window.location.href = data.payment_url
      }
    })
  }

  // Возврат из платёжного шлюза (/cart?order=…) сам по себе не значит, что
  // оплата прошла — шлюз редиректит на один и тот же return_url при отмене
  // или ошибке оплаты. Поэтому статус всегда сверяется с сервером, и форма
  // скрывается/корзина очищается только если заказ реально оплачен.
  public async confirmOrderPayment(orderId: string): Promise<void> {
    const form = this.container.querySelector('form') as HTMLFormElement
    const action = form?.dataset.orderStatusApi

    if (!action) return

    try {
      const response = await fetch(
        `${action}?order_id=${encodeURIComponent(orderId)}`,
        {
          method: 'GET',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        }
      )

      if (!response.ok) return

      const data = await response.json()

      if (data.status === 'paid') {
        this.onOrderComplete()
      }
    } catch {
      // Сетевая ошибка — оставляем форму как есть, не считаем заказ оплаченным.
    }
  }

  public onOrderComplete(): void {
    this.container.classList.add('is-hidden')
    this.list.reset()
    this.renderSuccessMessage()
  }

  private renderSuccessMessage(): void {
    const message = document.createElement('div')
    message.classList.add('cart-form__status')
    message.innerHTML = `
      <span class="cart-form__status-icon">
        <svg>
          <use href="#double-ok"></use>
        </svg>
      </span>
      <b class="cart-form__status-title">Заказ оформлен</b>
      <p class="cart-form__status-text">Мы&nbsp;свяжемся с&nbsp;вами в&nbsp;ближайшее время для подтверждения заказа.</p>
    `
    this.container.insertAdjacentElement('afterend', message)
  }

  private updateForm() {
    const deliveryGroup = this.container.querySelector<HTMLElement>(
      '[data-cart-group="delivery"]'
    )
    const pickupGroup = this.container.querySelector<HTMLElement>(
      '[data-cart-group="pickup"]'
    )
    const courierGroup = this.container.querySelector<HTMLElement>(
      '[data-cart-group="courier"]'
    )

    deliveryGroup?.classList.toggle(
      'is-hidden',
      this.deliveryMethod === 'postal'
    )
    this.changeElementsState(deliveryGroup!, this.deliveryMethod === 'postal')

    pickupGroup?.classList.toggle('is-hidden', this.deliveryMethod !== 'pickup')
    this.changeElementsState(pickupGroup!, this.deliveryMethod !== 'pickup')

    courierGroup?.classList.toggle(
      'is-hidden',
      this.deliveryMethod !== 'courier'
    )
    this.changeElementsState(courierGroup!, this.deliveryMethod !== 'courier')
  }

  private changeElementsState(group: HTMLElement, disabled: boolean) {
    const inputs = group.querySelectorAll('input, select, textarea')
    inputs.forEach((input) => {
      ;(input as HTMLInputElement).disabled = disabled
    })

    const complexInputs =
      group.querySelectorAll<HTMLElement>('[data-phone-input]')
    complexInputs.forEach((input) => {
      input.classList.toggle('disabled', disabled)
    })
  }

  private getCsrfToken(): string {
    return (
      document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
        ?.content ?? ''
    )
  }

  private async fetchForm(
    form: HTMLFormElement,
    action: string
  ): Promise<OrderFormSuccessResponse | null> {
    try {
      this.submitBtn.classList.add('loading')
      this.submitBtn.disabled = true

      const formData = this.getFormData(form)

      const stocks = String(formData.get('stocks'))
      if (getCartItems().some((item) => item.currency === 'BYN')) {
        if (!this.quoteToken || this.quotedStocks !== stocks) {
          const quoteResponse = await fetch('/api/certificate_quote', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'X-CSRF-Token': this.getCsrfToken() },
            body: formData
          })
          const quote = await quoteResponse.json()
          if (!quoteResponse.ok || !quote.success)
            throw new Error(quote.error_text || 'Не удалось рассчитать сумму')
          this.quoteToken = quote.quote_token
          this.quotedStocks = stocks
          let notice = form.querySelector<HTMLElement>(
            '[data-certificate-quote]'
          )
          if (!notice) {
            notice = document.createElement('p')
            notice.dataset.certificateQuote = ''
            notice.className = 'cart-form__quote'
            notice.setAttribute('role', 'status')
            this.submitBtn.parentElement?.before(notice)
          }
          notice.textContent = `К оплате ${Number(quote.amount).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} RUB по курсу ЦБ РФ от ${quote.rate_date}. Номинал сертификата сохраняется в BYN. Ваш банк может использовать свой курс конвертации. Расчёт действует 30 минут.`
          const label = this.submitBtn.querySelector('.button__text')
          if (label) label.textContent = 'Подтвердить и перейти к оплате'
          this.submitBtn.classList.remove('loading')
          this.submitBtn.disabled = false
          return null
        }
        formData.append('quote_token', this.quoteToken)
      }
      const response = await fetch(action || '', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': this.getCsrfToken()
        },
        body: formData
      })

      const data = (await response.json()) as OrderFormResponse

      // make_order возвращает { success: false, error_text } на любой сбой
      // (валидация, ошибка YooKassa).
      if (!response.ok || !data.success) {
        throw new Error(
          (!data.success && data.error_text) || 'Ошибка при отправке формы'
        )
      }

      return data
    } catch (error) {
      if ((error as Error).message.includes('устарел')) this.quoteToken = null
      showError(form, (error as Error).message)
      // На успехе кнопка остаётся disabled: страница уходит на оплату, и
      // повторный клик до навигации не должен создавать второй заказ и
      // второй реальный платёж (см. make_order — он не идемпотентен между
      // запросами, в отличие от Payments::CreateYookassaPayment внутри одного).
      this.submitBtn.classList.remove('loading')
      this.submitBtn.disabled = false
      return null
    }
  }

  private getFormData(form: HTMLFormElement): FormData {
    const formData = new FormData(form)
    const items = getCartItems()

    formData.append(
      'stocks',
      JSON.stringify(
        items.map((item) => ({
          id: item.id,
          type: item.type,
          parameters: item.parameters,
          quantity: item.quantity,
          currency: item.currency || 'RUB',
          price: item.price
        }))
      )
    )

    return formData
  }
}
